import type { AxiosResponse } from "axios";
import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";

export type RoutePresetSortDirection = "ASC" | "DESC";
export type RoutePresetRouteMode = "MANAGED_BY_IGNITION" | "MANAGED_BY_SERVER";
export type RoutePresetDetectionBasedOn = "SPEED" | "GPS_COORDS";
export type RoutePresetStartStopCriteria = "IGNITION" | "ONLY_CRITERIA";

export interface RoutePresetRecord {
  id: string | number;
  name?: string;
  isBase?: boolean;
  routeMode?: RoutePresetRouteMode;
  startStopCriteria?: RoutePresetStartStopCriteria;
  detectionBasedOn?: RoutePresetDetectionBasedOn;
  minSpeedKmh?: number | string | null;
  maxSpeedKmh?: number | string | null;
  geofenceMeters?: number | string | null;
  validationPoints?: number | string | null;
  idleTimeoutSec?: number | string | null;
  noDataTimeoutSec?: number | string | null;
  timeoutNoData?: number | string | null;
  stoppedTimeoutSec?: number | string | null;
}

export interface RoutePresetListParams {
  page?: number;
  size?: number;
  sort?: RoutePresetSortDirection;
  sortBy?: string;
}

export interface RoutePresetListResponse {
  presets?: RoutePresetRecord[];
  resources?: RoutePresetRecord[];
  totalItems?: number;
  totalElements?: number;
  total?: number;
  size?: number;
  page?: number;
}

export interface RoutePresetExportPayload {
  name: string;
  routeMode?: RoutePresetRouteMode | null;
  startStopCriteria?: RoutePresetStartStopCriteria | null;
  detectionBasedOn?: RoutePresetDetectionBasedOn | null;
  minSpeedKmh?: number | string | null;
  geofenceMeters?: number | string | null;
  validationPoints?: number | string | null;
  idleTimeoutSec?: number | string | null;
  noDataTimeoutSec?: number | string | null;
}

const ROUTE_PRESETS_PAGED_URL = "/ila/api/v1/route-preset/paged";
const ROUTE_PRESETS_DETAIL_URL = "/ila/api/v1/route-preset";
const ROUTE_PRESETS_LIST_URL = "/ila/api/v1/route-preset/presets";
const ROUTE_PRESETS_DELETE_URL = "/ila/api/v1/route-preset/presets";

const withParams = (url: string, params: Record<string, unknown>) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `${url}?${queryString}` : url;
};

export const getRoutePresetsPagedList = (
  params: RoutePresetListParams = {},
  language?: string
): Promise<AxiosResponse<{ response: RoutePresetListResponse }>> =>
  ApiService.get(withParams(ROUTE_PRESETS_PAGED_URL, { ...params }), getLanguageHeaders(language));

export const getRoutePresets = (
  language?: string
): Promise<AxiosResponse<{ response: RoutePresetRecord[] }>> =>
  ApiService.get(ROUTE_PRESETS_LIST_URL, getLanguageHeaders(language));

export const getRoutePresetById = (
  presetId: string | number,
  language?: string
): Promise<AxiosResponse<{ response: RoutePresetRecord | RoutePresetRecord[] | null }>> =>
  ApiService.get(`${ROUTE_PRESETS_DETAIL_URL}/${presetId}`, getLanguageHeaders(language));

export const cloneRoutePreset = (
  params: { id: string | number; name: string },
  language?: string
) =>
  ApiService.post(
    `${ROUTE_PRESETS_LIST_URL}/${params.id}/clone?name=${encodeURIComponent(params.name)}`,
    undefined,
    getLanguageHeaders(language)
  );

export const exportRoutePreset = (payload: RoutePresetExportPayload, language?: string) =>
  ApiService.post(ROUTE_PRESETS_LIST_URL, payload, getLanguageHeaders(language));

export const applyRoutePreset = (
  params: { id: string | number; vehicleId: string | number },
  language?: string
) =>
  ApiService.post(
    `${ROUTE_PRESETS_LIST_URL}/${params.id}/use?vehicleId=${params.vehicleId}`,
    undefined,
    getLanguageHeaders(language)
  );

export const deleteRoutePreset = (presetId: string | number, language?: string) =>
  ApiService.delete(`${ROUTE_PRESETS_DELETE_URL}/${presetId}`, getLanguageHeaders(language));
